import { BigNumber, Contract, utils } from 'ethers'
import { CHAIN_ID, REWARD_CONTROLLER } from './network'
import { MultiCallAddress } from '../contract'
import MULTICALL_ABI from '../../ethers/IMultiCall.json'

const CONTROLLER_ABI = [
    'function wallet() view returns (address)',
    'function USDT() view returns (address)',
    'function walletIndex(address) view returns (uint256 depositIndex, uint256 withdrawIndex)',
    'function depositUsdtToWallet(uint256 amount)',
]
const FACTORY_ABI = [
    'function implementation() view returns (address)',
    'function predictAddress(uint256) view returns (address)',
]
const TOKEN_ABI = [
    'function decimals() view returns (uint8)',
    'function balanceOf(address) view returns (uint256)',
    'function allowance(address,address) view returns (uint256)',
    'function approve(address,uint256) returns (bool)',
]

export function predictWalletAddress(factory, implementation, controller, id) {
    const codeHash = utils.keccak256(utils.hexConcat([
        '0x3d602d80600a3d3981f3', '0x363d3d373d3d3d363d73',
        implementation, '0x5af43d82803e903d91602b57fd5bf3',
    ]))
    const salt = utils.solidityKeccak256(['address', 'uint256'], [controller, id])
    return utils.getCreate2Address(factory, salt, codeHash)
}

async function requireNetwork(provider) {
    if ((await provider.getNetwork()).chainId !== CHAIN_ID) {
        throw new Error(`请切换到 BNB Smart Chain（Chain ID: ${CHAIN_ID}）`)
    }
}

export function parseDepositAmount(value, decimals) {
    if (!/^\d+(\.\d+)?$/.test(value.trim())) throw new Error('请输入有效的 USDT 数量')
    let amount
    try { amount = utils.parseUnits(value.trim(), decimals) }
    catch (_) { throw new Error(`数量最多支持 ${decimals} 位小数，且不能超出范围`) }
    if (amount.lte(0)) throw new Error('充值数量必须大于 0')
    return amount
}

export async function readWalletBalance(provider, account, onProgress = () => {}) {
    await requireNetwork(provider)
    const blockTag = await provider.getBlockNumber()
    const options = { blockTag }
    const controller = new Contract(REWARD_CONTROLLER, CONTROLLER_ABI, provider)
    const [factoryAddress, tokenAddress] = await Promise.all([controller.wallet(options), controller.USDT(options)])
    const factory = new Contract(factoryAddress, FACTORY_ABI, provider)
    const token = new Contract(tokenAddress, TOKEN_ABI, provider)
    const [indices, implementation, decimals, accountBalance] = await Promise.all([
        controller.walletIndex(tokenAddress, options), factory.implementation(options), token.decimals(options),
        account ? token.balanceOf(account, options) : Promise.resolve(null),
    ])
    const [depositIndex, withdrawIndex] = indices
    if (withdrawIndex.gt(depositIndex)) throw new Error('钱包索引异常，无法统计余额')
    const tokenId = BigNumber.from(tokenAddress)
    const addressAt = index => predictWalletAddress(factoryAddress, implementation, REWARD_CONTROLLER, tokenId.add(index))
    if (withdrawIndex.lt(depositIndex)) {
        const encoded = await provider.call({
            to: factoryAddress, from: REWARD_CONTROLLER,
            data: factory.interface.encodeFunctionData('predictAddress', [tokenId.add(withdrawIndex)]),
        }, blockTag)
        const [expected] = factory.interface.decodeFunctionResult('predictAddress', encoded)
        if (expected.toLowerCase() !== addressAt(withdrawIndex).toLowerCase()) {
            throw new Error('钱包地址校验失败，无法统计余额')
        }
    }
    const multi = new Contract(MultiCallAddress, MULTICALL_ABI, provider)
    let total = BigNumber.from(0)
    const count = depositIndex.sub(withdrawIndex)
    for (let start = withdrawIndex; start.lt(depositIndex); start = start.add(200)) {
        const end = start.add(200).lt(depositIndex) ? start.add(200) : depositIndex
        const calls = []
        for (let index = start; index.lt(end); index = index.add(1)) {
            calls.push([tokenAddress, token.interface.encodeFunctionData('balanceOf', [addressAt(index)])])
        }
        const [, results] = await multi.callStatic.aggregate(calls, options)
        if (results.length !== calls.length) throw new Error('余额查询不完整，请重试')
        for (const result of results) {
            total = total.add(token.interface.decodeFunctionResult('balanceOf', result)[0])
        }
        onProgress(`${end.sub(withdrawIndex).toString()} / ${count.toString()}`)
    }
    return { total, decimals, accountBalance, tokenAddress, blockTag, count: count.toString(),
        depositIndex: depositIndex.toString(), withdrawIndex: withdrawIndex.toString() }
}

async function waitConfirmed(tx) {
    try { return await tx.wait() }
    catch (error) {
        if (error.code === 'TRANSACTION_REPLACED' && !error.cancelled && error.receipt?.status === 1) return error.receipt
        throw error
    }
}

export async function depositWallet(signer, account, value, onStatus = () => {}) {
    const checkAccount = async () => {
        await requireNetwork(signer.provider)
        const accounts = await signer.provider.listAccounts()
        if (!accounts[0] || accounts[0].toLowerCase() !== account.toLowerCase()) throw new Error('钱包账户已变化，请重新操作')
    }
    await checkAccount()
    const controller = new Contract(REWARD_CONTROLLER, CONTROLLER_ABI, signer)
    const token = new Contract(await controller.USDT(), TOKEN_ABI, signer)
    const amount = parseDepositAmount(value, await token.decimals())
    const [balance, allowance] = await Promise.all([token.balanceOf(account), token.allowance(account, REWARD_CONTROLLER)])
    if (balance.lt(amount)) throw new Error('USDT 余额不足')
    if (allowance.lt(amount)) {
        onStatus('请确认 USDT 授权')
        const approval = await token.approve(REWARD_CONTROLLER, amount)
        onStatus('等待授权确认…')
        await waitConfirmed(approval)
    }
    await checkAccount()
    // Simulate before sending: an unsupported deployment must not fall back to a plain transfer.
    await controller.callStatic.depositUsdtToWallet(amount)
    onStatus('请确认充值交易')
    const tx = await controller.depositUsdtToWallet(amount)
    onStatus('等待充值到账…')
    return waitConfirmed(tx)
}
